import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDownRight, AlertTriangle, Receipt, CalendarClock, Landmark, Tags } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Cell, LineChart, Line, Legend } from "recharts";
import { useResource } from "@/lib/api";
import { MONTH_NAMES, formatAUD, roundCents, isNegative, sumDecimals } from "@/lib/format";
import { headline } from "@/components/settlement/headline";

const c = (v, a = 1) => `hsl(var(--${v}) / ${a})`;
const num = (v) => Number(roundCents(v ?? "0"));
const compact = (v) => `${v < 0 ? "-" : ""}$${new Intl.NumberFormat("en-AU", { notation: "compact", maximumFractionDigits: 1 }).format(Math.abs(v))}`;
const tip = { background: c("popover"), border: `1px solid ${c("border")}`, borderRadius: 12, color: c("foreground") };

const Delta = ({ now, before, invert, testId }) => {
  if (before == null) return null;
  const diff = sumDecimals([now, String(before).startsWith("-") ? String(before).slice(1) : `-${before}`]);
  const up = !isNegative(diff);
  const good = invert ? !up : up;
  const pct = num(before) !== 0 ? Math.abs((num(diff) / Math.abs(num(before))) * 100).toFixed(1) : null;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-mono ${good ? "bg-emerald-500/10 text-emerald-500" : "bg-rose-500/10 text-rose-500"}`} data-testid={testId}>
      <Icon className="h-3.5 w-3.5" />{up ? "+" : ""}{formatAUD(diff)}{pct && ` (${pct}%)`}
    </span>
  );
};

const Kpi = ({ label, value, sub, delta, testId }) => (
  <div className="border border-border bg-card p-5" data-testid={testId}>
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`font-mono text-2xl mt-2 break-all ${isNegative(value) ? "text-rose-500" : ""}`}>{formatAUD(value)}</div>
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">{delta}{sub}</div>
  </div>
);

const YearsChart = ({ years, current }) => (
  <div className="border border-border bg-card p-5 lg:col-span-2" data-testid="dashboard-years-chart">
    <h2 className="font-display text-lg font-semibold">Profit by financial year</h2>
    <p className="text-xs text-muted-foreground mt-1">Net profit (income less expenses). {current} is the current year, so far.</p>
    <div className="h-64 mt-4">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={years.map((y) => ({ fy: y.fy, Income: num(y.income), Expenses: num(y.expenses), Net: num(y.net) }))} barGap={2}>
          <CartesianGrid stroke={c("border")} vertical={false} />
          <XAxis dataKey="fy" stroke={c("muted-foreground")} fontSize={12} tickLine={false} axisLine={false} />
          <YAxis stroke={c("muted-foreground")} fontSize={11} tickLine={false} axisLine={false} width={56} tickFormatter={compact} />
          <ReferenceLine y={0} stroke={c("muted-foreground", 0.5)} />
          <Tooltip cursor={{ fill: c("accent", 0.5) }} formatter={(v) => formatAUD(String(v))} contentStyle={tip} />
          <Bar dataKey="Net" radius={[8, 8, 0, 0]} maxBarSize={56}>
            {years.map((y) => <Cell key={y.fy} fill={y.fy === current ? c("primary") : isNegative(y.net) ? c("neg", 0.7) : c("chart-muted")} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const TrendChart = ({ ytd, current }) => (
  <div className="border border-border bg-card p-5" data-testid="dashboard-trend-chart">
    <h2 className="font-display text-lg font-semibold">Month by month</h2>
    <p className="text-xs text-muted-foreground mt-1">Net profit {current} against {ytd.prev_fy || "last year"}.</p>
    <div className="h-64 mt-4">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={ytd.trend.map((m, i) => ({ name: MONTH_NAMES[m.month - 1], This: i < ytd.months ? num(m.net) : null, Last: num(m.prev_net) }))}>
          <CartesianGrid stroke={c("border")} vertical={false} />
          <XAxis dataKey="name" stroke={c("muted-foreground")} fontSize={11} tickLine={false} axisLine={false} />
          <YAxis stroke={c("muted-foreground")} fontSize={11} tickLine={false} axisLine={false} width={52} tickFormatter={compact} />
          <ReferenceLine y={0} stroke={c("muted-foreground", 0.5)} />
          <Tooltip formatter={(v) => formatAUD(String(v))} contentStyle={tip} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line dataKey="Last" name={ytd.prev_fy || "Last year"} stroke={c("info")} strokeDasharray="6 4" strokeWidth={2} dot={false} />
          <Line dataKey="This" name={current} stroke={c("primary")} strokeWidth={2.5} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const Attention = ({ d }) => {
  const items = [
    { icon: AlertTriangle, label: "Data quality issues", value: d.issues.count, extra: d.issues.double_ups ? `${d.issues.double_ups} possible double-ups` : "", to: "/data-quality", id: "dq" },
    { icon: Tags, label: `Uncategorised rows in ${d.current_fy}`, value: d.uncategorised, to: "/ledger", id: "uncat" },
    { icon: Receipt, label: "Expenses to invoice", value: formatAUD(d.to_invoice.total), extra: `${d.to_invoice.count} lines`, to: "/invoicing", id: "invoice" },
    { icon: CalendarClock, label: "Forecast still expected", value: formatAUD(d.forecast_expected.total), extra: `${d.forecast_expected.count} items`, to: "/forecast", id: "forecast" },
    { icon: Landmark, label: "Bank feed up to", value: d.bank.last_bank_date || "Not started", to: "/bank-feed", id: "bank" },
  ];
  return (
    <div className="border border-border bg-card p-5" data-testid="dashboard-attention">
      <h2 className="font-display text-lg font-semibold">Needs attention</h2>
      <div className="mt-3">
        {items.map(({ icon: Icon, label, value, extra, to, id }) => (
          <Link key={id} to={to} className="flex items-center gap-3 py-2.5 border-b border-border/60 last:border-0 hover:text-primary transition-colors" data-testid={`dashboard-attention-${id}`}>
            <span className="h-8 w-8 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0"><Icon className="h-4 w-4" /></span>
            <span className="text-sm flex-1">{label}{extra && <span className="block text-xs text-muted-foreground">{extra}</span>}</span>
            <span className="font-mono text-sm">{value}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

const DirectorStanding = ({ directors, companyNet }) => (
  <div className="grid gap-4 md:grid-cols-3" data-testid="dashboard-directors">
    {directors.map((dr) => {
      const h = headline(dr.result);
      return (
        <Link to="/settlement" key={dr.id} className="border border-border bg-card p-5 hover:border-primary/50 transition-colors" data-testid={`dashboard-director-${dr.name.toLowerCase()}`}>
          <div className="flex items-center justify-between">
            <span className="font-display text-lg font-semibold">{dr.name}</span>
            <span className="text-xs text-muted-foreground">all years</span>
          </div>
          <div className={`mt-2 text-base font-semibold ${h.tone.replace("-300", "-500")}`}>{h.text}</div>
          <div className="text-xs text-muted-foreground mt-1">Share of company net {formatAUD(companyNet)} plus their own credits and debits</div>
        </Link>
      );
    })}
  </div>
);

export default function DashboardPage() {
  const { data: d } = useResource("/dashboard");
  if (!d) return <div className="text-muted-foreground" data-testid="dashboard-loading">Loading…</div>;
  if (!d.years.length) return <div className="text-muted-foreground">Set up a financial year to see the dashboard.</div>;
  const y = d.ytd;
  const to = y.to_month ? MONTH_NAMES[y.to_month - 1] : "";
  return (
    <div className="rise space-y-6">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold" data-testid="dashboard-title">Dashboard</h1>
        <p className="mt-3 text-muted-foreground text-base md:text-lg">FY {d.current_fy}, July to {to || "now"}, compared with the same months of {y.prev_fy || "last year"}.</p>
      </div>
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Net profit so far" value={y.net} testId="dashboard-kpi-net" delta={<Delta now={y.net} before={y.prev_net} testId="dashboard-delta-net" />} sub={y.prev_fy && `vs ${formatAUD(y.prev_net)}`} />
        <Kpi label="Income so far" value={y.income} testId="dashboard-kpi-income" delta={<Delta now={y.income} before={y.prev_income} testId="dashboard-delta-income" />} />
        <Kpi label="Expenses so far" value={y.expenses} testId="dashboard-kpi-expenses" delta={<Delta now={y.expenses} before={y.prev_expenses} invert testId="dashboard-delta-expenses" />} />
        <Kpi label="Company net, all years" value={d.company_net} testId="dashboard-kpi-company" sub="split between the directors" />
      </div>
      <div>
        <h2 className="font-display text-xl font-semibold mb-3">Where each director stands</h2>
        <DirectorStanding directors={d.directors} companyNet={d.company_net} />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <YearsChart years={d.years} current={d.current_fy} />
        <Attention d={d} />
      </div>
      <TrendChart ytd={y} current={d.current_fy} />
    </div>
  );
}
